import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { FloatingThemeToggleLayout } from './components/ThemeToggle';
import { LoginPage } from './features/auth/LoginPage';
import { TwoFactorSetupPage } from './features/auth/TwoFactorSetupPage';
import { ChangePasswordPage } from './features/auth/ChangePasswordPage';
import { SecurityPage } from './features/auth/SecurityPage';
import { ImpersonatePage } from './features/auth/ImpersonatePage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { QueuePage } from './features/opd/QueuePage';
import { TriagePage } from './features/opd/TriagePage';
import { CosignPage } from './features/opd/CosignPage';
import { OpdDuesPage } from './features/billing/OpdDuesPage';
import { AppointmentsPage } from './features/opd/AppointmentsPage';
import { BookAppointmentPage } from './features/opd/BookAppointmentPage';
import { WalkInPage } from './features/opd/WalkInPage';
import { ConsultationPage } from './features/opd/ConsultationPage';
import { SchedulesPage } from './features/opd/SchedulesPage';
import { SchedulePublishPage } from './features/opd/SchedulePublishPage';
import { ScheduleLeavePage } from './features/opd/ScheduleLeavePage';
import { ScreeningPage } from './features/opd/ScreeningPage';
import { IssueCertificatePage } from './features/opd/IssueCertificatePage';
import { FollowUpsPage } from './features/opd/FollowUpsPage';
import { HomePage } from './features/public/home/HomePage';
import { PublicLayout } from './features/public/PublicLayout';
import { AddPersonPage } from './features/public/AddPersonPage';
import { RequestAppointmentPage } from './features/public/RequestAppointmentPage';
import { CheckoutPage } from './features/public/CheckoutPage';
import { FindPage } from './features/public/FindPage';
import { OrgPage } from './features/public/OrgPage';
import { BranchPage } from './features/public/BranchPage';
import { MyActivityPage } from './features/public/MyActivityPage';
import { PublicListingPage } from './features/directory/PublicListingPage';
import { RequestsInboxPage } from './features/directory/RequestsInboxPage';
import { OnlineOrdersPage } from './features/directory/OnlineOrdersPage';
import { PatientsPage } from './features/patients/PatientsPage';
import { PatientRegisterPage } from './features/patients/PatientRegisterPage';
import { PatientDetailPage } from './features/patients/PatientDetailPage';
import { PatientEditPage } from './features/patients/PatientEditPage';
import { PatientContactPage } from './features/patients/PatientContactPage';
import { PatientAllergyPage } from './features/patients/PatientAllergyPage';
import { BillingPage } from './features/billing/BillingPage';
import { InvoiceDetailPage } from './features/billing/InvoiceDetailPage';
import { ShiftPage } from './features/billing/ShiftPage';
import { RefundsPage } from './features/billing/RefundsPage';
import { StatementPage } from './features/billing/StatementPage';
import { CustomerCreditPage } from './features/billing/CustomerCreditPage';
import { ChargeMasterPage } from './features/billing/ChargeMasterPage';
import { BillingSettingsPage } from './features/billing/BillingSettingsPage';
import { ChargeItemPage } from './features/billing/ChargeItemPage';
import { TariffPage } from './features/billing/TariffPage';
import { PackagePage } from './features/billing/PackagePage';
import { NewInvoicePage } from './features/billing/NewInvoicePage';
import { CreditNotePage } from './features/billing/CreditNotePage';
import { PharmacyPage } from './features/pharmacy/PharmacyPage';
import { InventoryPage } from './features/pharmacy/InventoryPage';
import { PayablesPage } from './features/pharmacy/PayablesPage';
import { StockControlPage } from './features/pharmacy/StockControlPage';
import { LabPage } from './features/diagnostics/LabPage';
import { LabOrderPage } from './features/diagnostics/LabOrderPage';
import { RadiologyPage } from './features/diagnostics/RadiologyPage';
import { RadiologyOrderPage } from './features/diagnostics/RadiologyOrderPage';
import { RadiologyReportPage } from './features/diagnostics/RadiologyReportPage';
import { BedBoardPage } from './features/ipd/BedBoardPage';
import { AdmissionsPage } from './features/ipd/AdmissionsPage';
import { AdmitPage } from './features/ipd/AdmitPage';
import { AdmissionWorkspace } from './features/ipd/admission/AdmissionWorkspace';
import { IpdEmrPage } from './features/ipd/emr/IpdEmrPage';
import { IpdOrderEntryPage } from './features/ipd/emr/orders/IpdOrderEntryPage';
import { IpdNotePage } from './features/ipd/IpdNotePage';
import { DischargePage } from './features/ipd/DischargePage';
import { WardChargeRulesPage } from './features/ipd/WardChargeRulesPage';
import { WardChargeRulePage } from './features/ipd/WardChargeRulePage';
import { DuesRegisterPage } from './features/ipd/DuesRegisterPage';
import { DuesFollowUpPage } from './features/ipd/DuesFollowUpPage';
import { VisitorLocatorPage } from './features/ipd/VisitorLocatorPage';
import { WardPickerPage } from './features/ipd/tasks/WardPickerPage';
import { WardTaskBoardPage } from './features/ipd/tasks/WardTaskBoardPage';
import { ErBoardPage } from './features/emergency/ErBoardPage';
import { ErRegisterPage } from './features/emergency/ErRegisterPage';
import { ErTriagePage } from './features/emergency/ErTriagePage';
import { ErDispositionPage } from './features/emergency/ErDispositionPage';
import { TheatrePage } from './features/theatre/TheatrePage';
import { ScheduleCasePage } from './features/theatre/ScheduleCasePage';
import { CasePage } from './features/theatre/CasePage';
import { BloodBankPage } from './features/bloodbank/BloodBankPage';
import { DentalPage, MaternityPage, TherapyPage } from './features/specialty/SpecialtyPages';
import {
  AmbulancePage,
  DietaryPage,
  FeedbackPage,
  HousekeepingPage,
  TasksPage,
} from './features/support/SupportPages';
import { KioskPage, PortalApp } from './features/portal/PortalApp';
import { TeleRoomPage } from './features/portal/TeleRoomPage';
import { AssetsPage } from './features/assets/AssetsPage';
import { QcPage } from './features/diagnostics/QcPage';
import { QcLotPage } from './features/diagnostics/QcLotPage';
import { QcRunPage } from './features/diagnostics/QcRunPage';
import { LabCataloguePage } from './features/diagnostics/LabCataloguePage';
import { LabTestPage } from './features/diagnostics/LabTestPage';
import { LabPanelPage } from './features/diagnostics/LabPanelPage';
import { LabInstrumentPage } from './features/diagnostics/LabInstrumentPage';
import { LabReportsPage } from './features/diagnostics/LabReportsPage';
import { CriticalValuesPage } from './features/diagnostics/CriticalValuesPage';
import { HomeCollectionPage } from './features/diagnostics/HomeCollectionPage';
import { InsurancePage } from './features/insurance/InsurancePage';
import { PayerFormPage } from './features/insurance/PayerFormPage';
import { PayerManagePage } from './features/insurance/PayerManagePage';
import { CoveragePage } from './features/insurance/CoveragePage';
import { PreAuthNewPage } from './features/insurance/PreAuthNewPage';
import { PreAuthRespondPage } from './features/insurance/PreAuthRespondPage';
import { PreAuthExtendPage } from './features/insurance/PreAuthExtendPage';
import { PreAuthEditPage } from './features/insurance/PreAuthEditPage';
import { RecordRemittancePage } from './features/insurance/RecordRemittancePage';
import { AssembleClaimPage } from './features/insurance/AssembleClaimPage';
import { ClaimPeriodPage } from './features/insurance/ClaimPeriodPage';
import { ReplyPage } from './features/insurance/ReplyPage';
import { AccountingPage } from './features/accounting/AccountingPage';
import { PayoutsPage } from './features/accounting/PayoutsPage';
import { FinancialAuthorityPage } from './features/accounts/FinancialAuthorityPage';
import { DoctorSharePage } from './features/accounts/DoctorSharePage';
import { VoucherPage } from './features/accounting/VoucherPage';
import { AccountPage } from './features/accounting/AccountPage';
import { ComputePayoutPage } from './features/accounting/ComputePayoutPage';
import { PayoutAdjustmentPage } from './features/accounting/PayoutAdjustmentPage';
import { ShareChangePage } from './features/accounts/ShareChangePage';
import { AuthorityChangePage } from './features/accounts/AuthorityChangePage';
import { PeoplePage } from './features/people/PeoplePage';
import { PersonPage } from './features/people/PersonPage';
import { RolePage } from './features/people/RolePage';
import { RequestLeavePage } from './features/hr/RequestLeavePage';
import { MarkAttendancePage } from './features/hr/MarkAttendancePage';
import { RunPayrollPage } from './features/hr/RunPayrollPage';
import { NewRosterPage } from './features/hr/NewRosterPage';
import { AssignShiftPage } from './features/hr/AssignShiftPage';
import { MrdPage } from './features/mrd/MrdPage';
import { LogRequestPage } from './features/mrd/LogRequestPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { BranchesPage } from './features/admin/BranchesPage';
import { BranchFormPage } from './features/admin/BranchFormPage';
import { DepartmentPage } from './features/admin/DepartmentPage';
import { SpecialtyPage } from './features/admin/SpecialtyPage';
import { StorePage } from './features/admin/StorePage';
import { WardPage } from './features/admin/WardPage';
import { BedPage } from './features/admin/BedPage';
import { ListingPage } from './features/directory/ListingPage';
import { SettingsPage } from './features/admin/SettingsPage';
import { DepartmentPhrasesPage } from './features/admin/DepartmentPhrasesPage';
import { AuditPage } from './features/admin/AuditPage';
import { OwnerPortal } from './features/owner/OwnerPortal';
import { OwnerOverviewPage } from './features/owner/OwnerOverviewPage';
import { TenantsPage } from './features/owner/TenantsPage';
import { OwnerTrafficPage } from './features/owner/OwnerTrafficPage';
import { OwnerAccountsPage } from './features/owner/OwnerAccountsPage';
import { TenantDetailPage } from './features/owner/TenantDetailPage';
import { ProvisionTenantPage } from './features/owner/ProvisionTenantPage';
import { EditTenantPage } from './features/owner/EditTenantPage';
import { TenantClosurePage } from './features/owner/TenantClosurePage';
import { PlanFormPage } from './features/owner/PlanFormPage';
import { PlatformStaffFormPage } from './features/owner/PlatformStaffFormPage';
import { EditReleasePage } from './features/owner/EditReleasePage';
import { AnnounceReleasePage } from './features/owner/AnnounceReleasePage';
import { FacilityRequestFormPage } from './features/owner/FacilityRequestFormPage';
import { FacilityRequestsPage } from './features/owner/FacilityRequestsPage';
import { BranchRequestsPage } from './features/owner/BranchRequestsPage';
import { ContactsPage } from './features/owner/ContactsPage';
import { OwnerAuditPage } from './features/owner/OwnerAuditPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';
import { TemplatePage } from './features/notifications/TemplatePage';
import { SendMessagePage } from './features/notifications/SendMessagePage';
import { PlansPage } from './features/owner/PlansPage';
import { PersonChangesPage } from './features/owner/PersonChangesPage';
import { PlatformStaffPage } from './features/owner/PlatformStaffPage';
import { DeletionBackupsPage } from './features/owner/DeletionBackupsPage';
import { DatabaseBackupsPage } from './features/owner/DatabaseBackupsPage';
import { OwnerTicketsPage } from './features/owner/OwnerTicketsPage';
import { OwnerRenewalsPage } from './features/owner/OwnerRenewalsPage';
import { OwnerAlertsPage } from './features/owner/OwnerAlertsPage';
import { OwnerMailPage } from './features/owner/mail/OwnerMailPage';
import { OwnerSyncStatusPage } from './features/owner/OwnerSyncStatusPage';
import { OwnerWatchPage } from './features/owner/OwnerWatchPage';
import { OwnerReleasesPage } from './features/owner/OwnerReleasesPage';
import { TenantSupportPage } from './features/desk/TenantSupportPage';
import { TenantNewTicketPage } from './features/desk/NewTicketPage';
import { ClientHomePage } from './features/client/ClientHomePage';
import { ClientTicketsPage } from './features/client/ClientTicketsPage';
import { ClientNewTicketPage } from './features/client/ClientNewTicketPage';
import { ClientRenewalsPage } from './features/client/ClientRenewalsPage';
import { RenewalPage } from './features/client/RenewalPage';
import { ProductPage } from './features/pharmacy/ProductPage';
import { SupplierPage } from './features/pharmacy/SupplierPage';
import { ReceiveGoodsPage } from './features/pharmacy/ReceiveGoodsPage';
import { NewPurchaseOrderPage } from './features/pharmacy/NewPurchaseOrderPage';
import { NewIndentPage } from './features/pharmacy/NewIndentPage';
import { PaySupplierPage } from './features/pharmacy/PaySupplierPage';
import { ReturnGoodsPage } from './features/pharmacy/ReturnGoodsPage';
import { AdjustStockPage } from './features/pharmacy/AdjustStockPage';
import { TransferStockPage } from './features/pharmacy/TransferStockPage';
import { BatchStatusPage } from './features/pharmacy/BatchStatusPage';
import { NewCountSheetPage } from './features/pharmacy/NewCountSheetPage';
import { OpenLabourPage } from './features/specialty/OpenLabourPage';
import { CompleteDeliveryPage } from './features/specialty/CompleteDeliveryPage';
import { BookSessionPage } from './features/specialty/BookSessionPage';
import { ToothTreatmentPage } from './features/specialty/ToothTreatmentPage';
import { AssignTaskPage } from './features/support/AssignTaskPage';
import { DispatchTripPage } from './features/support/DispatchTripPage';
import { RaiseComplaintPage } from './features/support/RaiseComplaintPage';
import { AddAssetPage } from './features/assets/AddAssetPage';
import { SchedulePage } from './features/assets/SchedulePage';
import { RaiseTicketPage } from './features/assets/RaiseTicketPage';
import { RegisterDonorPage } from './features/bloodbank/RegisterDonorPage';
import { CrossMatchRequestPage } from './features/bloodbank/CrossMatchRequestPage';
import { QueueDisplayPage } from './features/opd/QueueDisplayPage';
import { NotFoundPage } from './features/NotFoundPage';

export function App() {
  return (
    <Routes>
      {/* Public landing page — no session, no shell. Staff reach the console
          from its "Sign in" CTA; the dashboard itself lives at /dashboard. */}
      <Route path="/" element={<HomePage />} />
      <Route element={<FloatingThemeToggleLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/two-factor" element={<TwoFactorSetupPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="/kiosk" element={<KioskPage />} />
        {/* The token in the URL is the credential; nothing else is asked for. */}
        <Route path="/tele/:token" element={<TeleRoomPage />} />
      </Route>
      {/* Waiting-room board: no sign-in, no patient identifiers. */}
      <Route path="/display/:branchId" element={<QueueDisplayPage />} />
      {/* Patient-facing surfaces: their own shell, their own token store. */}
      <Route path="/portal/*" element={<PortalApp />} />

      {/* The public directory. No hospital, no staff session — a patient
          arrives with a city and finds one. */}
      <Route path="/find" element={<PublicLayout />}>
        <Route index element={<FindPage />} />
        <Route path="me" element={<MyActivityPage />} />
        <Route path="me/people/new" element={<AddPersonPage />} />
        <Route path=":slug" element={<OrgPage />} />
        <Route path=":slug/:branchId" element={<BranchPage />} />
        <Route path=":slug/:branchId/book" element={<RequestAppointmentPage />} />
        <Route path=":slug/:branchId/checkout" element={<CheckoutPage />} />
      </Route>

      {/* Owner console — LIMS-parity: its own surface with its own sign-in
          (email + TOTP), outside the hospital shell. See OwnerPortal.tsx. */}
      <Route path="/owner" element={<OwnerPortal />}>
        <Route index element={<OwnerOverviewPage />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/new" element={<ProvisionTenantPage />} />
        <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
        <Route path="tenants/:tenantId/edit" element={<EditTenantPage />} />
        <Route path="tenants/:tenantId/closure" element={<TenantClosurePage />} />
        <Route path="tenants/:tenantId/watch/:sessionId" element={<OwnerWatchPage />} />
        <Route path="traffic" element={<OwnerTrafficPage />} />
        <Route path="owners" element={<OwnerAccountsPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="plans/:planId/edit" element={<PlanFormPage />} />
        <Route path="person-changes" element={<PersonChangesPage />} />
        <Route path="staff" element={<PlatformStaffPage />} />
        <Route path="staff/new" element={<PlatformStaffFormPage />} />
        <Route path="staff/:staffId/edit" element={<PlatformStaffFormPage />} />
        <Route path="tickets" element={<OwnerTicketsPage />} />
        <Route path="renewals" element={<OwnerRenewalsPage />} />
        <Route path="alerts" element={<OwnerAlertsPage />} />
        <Route path="mail" element={<OwnerMailPage />} />
        <Route path="offline-installs" element={<OwnerSyncStatusPage />} />
        <Route path="releases" element={<OwnerReleasesPage />} />
        <Route path="releases/announce" element={<AnnounceReleasePage />} />
        <Route path="releases/:releaseId/edit" element={<EditReleasePage />} />
        <Route path="deletion-backups" element={<DeletionBackupsPage />} />
        <Route path="backups" element={<DatabaseBackupsPage />} />
        <Route path="facility-requests" element={<FacilityRequestsPage />} />
        <Route path="branch-requests" element={<BranchRequestsPage />} />
        <Route path="facility-requests/new" element={<FacilityRequestFormPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="audit" element={<OwnerAuditPage />} />
      </Route>

      <Route element={<AppShell />}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="queue" element={<QueuePage />} />
        <Route path="triage" element={<TriagePage />} />
        <Route path="triage/:appointmentId/screening" element={<ScreeningPage />} />
        <Route path="cosign" element={<CosignPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="appointments/new" element={<BookAppointmentPage />} />
        <Route path="appointments/walk-in" element={<WalkInPage />} />
        <Route path="schedules" element={<SchedulesPage />} />
        <Route path="schedules/new" element={<SchedulePublishPage />} />
        <Route path="schedules/:scheduleId/edit" element={<SchedulePublishPage />} />
        <Route path="schedules/leave/new" element={<ScheduleLeavePage />} />
        <Route path="follow-ups" element={<FollowUpsPage />} />
        <Route path="online-requests" element={<RequestsInboxPage />} />
        <Route path="online-orders" element={<OnlineOrdersPage />} />
        <Route path="consultations" element={<QueuePage />} />
        <Route path="consultations/:encounterId" element={<ConsultationPage />} />
        <Route path="consultations/:encounterId/certificates/new" element={<IssueCertificatePage />} />

        <Route path="patients" element={<PatientsPage />} />
        <Route path="patients/new" element={<PatientRegisterPage />} />
        <Route path="patients/:patientId" element={<PatientDetailPage />} />
        <Route path="patients/:patientId/edit" element={<PatientEditPage />} />
        <Route path="patients/:patientId/coverages/new" element={<CoveragePage />} />
        <Route path="patients/:patientId/coverages/:coverageId/edit" element={<CoveragePage />} />
        <Route path="patients/:patientId/contacts/new" element={<PatientContactPage />} />
        <Route path="patients/:patientId/allergies/new" element={<PatientAllergyPage />} />

        <Route path="billing" element={<BillingPage />} />
        <Route path="billing/shift" element={<ShiftPage />} />
        <Route path="billing/opd-dues" element={<OpdDuesPage />} />
        <Route path="billing/refunds" element={<RefundsPage />} />
        <Route path="billing/statement" element={<StatementPage />} />
        <Route path="billing/credit-customers" element={<CustomerCreditPage />} />
        <Route path="billing/charges" element={<ChargeMasterPage />} />
        <Route path="billing/charges/services/new" element={<ChargeItemPage />} />
        <Route path="billing/charges/services/:id/edit" element={<ChargeItemPage />} />
        <Route path="billing/charges/price-lists/new" element={<TariffPage />} />
        <Route path="billing/charges/price-lists/:id" element={<TariffPage />} />
        <Route path="billing/charges/packages/new" element={<PackagePage />} />
        <Route path="billing/charges/packages/:id/edit" element={<PackagePage />} />
        <Route path="billing/settings" element={<BillingSettingsPage />} />
        <Route path="billing/invoices/new" element={<NewInvoicePage />} />
        <Route path="billing/invoices/:invoiceId" element={<InvoiceDetailPage />} />
        <Route path="billing/invoices/:invoiceId/credit-note" element={<CreditNotePage />} />

        <Route path="pharmacy" element={<PharmacyPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/products/new" element={<ProductPage />} />
        <Route path="inventory/products/:productId/edit" element={<ProductPage />} />
        <Route path="inventory/suppliers/new" element={<SupplierPage />} />
        <Route path="inventory/suppliers/:supplierId/edit" element={<SupplierPage />} />
        <Route path="inventory/grn/new" element={<ReceiveGoodsPage />} />
        <Route path="inventory/purchase-orders/new" element={<NewPurchaseOrderPage />} />
        <Route path="inventory/indents/new" element={<NewIndentPage />} />
        <Route path="payables" element={<PayablesPage />} />
        <Route path="payables/payments/new" element={<PaySupplierPage />} />
        <Route path="payables/returns/new" element={<ReturnGoodsPage />} />
        <Route path="stock-control" element={<StockControlPage />} />
        <Route path="stock-control/adjust" element={<AdjustStockPage />} />
        <Route path="stock-control/transfer" element={<TransferStockPage />} />
        <Route path="stock-control/batch-status" element={<BatchStatusPage />} />
        <Route path="stock-control/count-sheets/new" element={<NewCountSheetPage />} />
        <Route path="lab" element={<LabPage />} />
        <Route path="lab/orders/new" element={<LabOrderPage />} />
        <Route path="radiology" element={<RadiologyPage />} />
        <Route path="radiology/orders/new" element={<RadiologyOrderPage />} />
        <Route path="radiology/studies/:studyId/report" element={<RadiologyReportPage />} />
        <Route path="lab/qc" element={<QcPage />} />
        <Route path="lab/qc/lots/new" element={<QcLotPage />} />
        <Route path="lab/qc/runs/new" element={<QcRunPage />} />
        <Route path="lab/catalogue" element={<LabCataloguePage />} />
        <Route path="lab/catalogue/tests/new" element={<LabTestPage />} />
        <Route path="lab/catalogue/tests/:testId/edit" element={<LabTestPage />} />
        <Route path="lab/catalogue/panels/new" element={<LabPanelPage />} />
        <Route path="lab/catalogue/panels/:panelId/edit" element={<LabPanelPage />} />
        <Route path="lab/instruments/new" element={<LabInstrumentPage />} />
        <Route path="lab/instruments/:instrumentId/edit" element={<LabInstrumentPage />} />
        <Route path="lab/reports" element={<LabReportsPage />} />
        <Route path="lab/critical" element={<CriticalValuesPage />} />
        <Route path="lab/home-collection" element={<HomeCollectionPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="assets/new" element={<AddAssetPage />} />
        <Route path="assets/schedules/new" element={<SchedulePage />} />
        <Route path="assets/tickets/new" element={<RaiseTicketPage />} />

        <Route path="ipd/beds" element={<BedBoardPage />} />
        <Route path="ipd/admissions" element={<AdmissionsPage />} />
        <Route path="ipd/admissions/new" element={<AdmitPage />} />
        <Route path="ipd/admissions/:admissionId" element={<AdmissionWorkspace />} />
        <Route path="ipd/admissions/:admissionId/emr" element={<IpdEmrPage />} />
        <Route path="ipd/admissions/:admissionId/orders/new" element={<IpdOrderEntryPage />} />
        <Route path="ipd/admissions/:admissionId/notes/new" element={<IpdNotePage />} />
        <Route path="ipd/admissions/:admissionId/discharge" element={<DischargePage />} />
        <Route path="ipd/charge-rules" element={<WardChargeRulesPage />} />
        <Route path="ipd/charge-rules/new" element={<WardChargeRulePage />} />
        <Route path="ipd/charge-rules/:ruleId/edit" element={<WardChargeRulePage />} />
        <Route path="ipd/dues" element={<DuesRegisterPage />} />
        <Route path="ipd/dues/:admissionId/follow-up" element={<DuesFollowUpPage />} />
        <Route path="ipd/locator" element={<VisitorLocatorPage />} />
        <Route path="ipd/wards" element={<WardPickerPage />} />
        <Route path="ipd/wards/:wardId/tasks" element={<WardTaskBoardPage />} />
        <Route path="er" element={<ErBoardPage />} />
        <Route path="er/new" element={<ErRegisterPage />} />
        <Route path="er/:caseId/triage" element={<ErTriagePage />} />
        <Route path="er/:caseId/disposition" element={<ErDispositionPage />} />
        <Route path="ot" element={<TheatrePage />} />
        <Route path="ot/cases/new" element={<ScheduleCasePage />} />
        <Route path="ot/cases/:caseId" element={<CasePage />} />

        <Route path="blood-bank" element={<BloodBankPage />} />
        <Route path="blood-bank/donors/new" element={<RegisterDonorPage />} />
        <Route path="blood-bank/requests/new" element={<CrossMatchRequestPage />} />
        <Route path="maternity" element={<MaternityPage />} />
        <Route path="maternity/deliveries/new" element={<OpenLabourPage />} />
        <Route path="maternity/deliveries/:deliveryId/complete" element={<CompleteDeliveryPage />} />
        <Route path="therapy" element={<TherapyPage />} />
        <Route path="therapy/sessions/new" element={<BookSessionPage />} />
        <Route path="dental" element={<DentalPage />} />
        <Route path="dental/treatments/new" element={<ToothTreatmentPage />} />
        <Route path="ambulance" element={<AmbulancePage />} />
        <Route path="ambulance/trips/new" element={<DispatchTripPage />} />
        <Route path="dietary" element={<DietaryPage />} />
        <Route path="housekeeping" element={<HousekeepingPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="feedback/new" element={<RaiseComplaintPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="tasks/new" element={<AssignTaskPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="notifications/send" element={<SendMessagePage />} />
        <Route path="notifications/templates/new" element={<TemplatePage />} />
        <Route path="notifications/templates/:templateId/edit" element={<TemplatePage />} />

        <Route path="insurance" element={<InsurancePage />} />
        <Route path="insurance/payers/new" element={<PayerFormPage />} />
        <Route path="insurance/payers/:payerId" element={<PayerManagePage />} />
        <Route path="insurance/payers/:payerId/edit" element={<PayerFormPage />} />
        <Route path="insurance/pre-auths/new" element={<PreAuthNewPage />} />
        <Route path="insurance/pre-auths/extend" element={<PreAuthExtendPage />} />
        <Route path="insurance/pre-auths/:preAuthId/respond" element={<PreAuthRespondPage />} />
        <Route path="insurance/pre-auths/:preAuthId/edit" element={<PreAuthEditPage />} />
        <Route path="insurance/remittances/new" element={<RecordRemittancePage />} />
        <Route path="insurance/claims/new" element={<AssembleClaimPage />} />
        <Route path="insurance/claims/periods/new" element={<ClaimPeriodPage />} />
        <Route path="insurance/requests/:requestId/reply" element={<ReplyPage />} />
        <Route path="accounting" element={<AccountingPage />} />
        <Route path="accounting/journals/new" element={<VoucherPage />} />
        <Route path="accounting/accounts/new" element={<AccountPage />} />
        <Route path="accounting/payouts" element={<PayoutsPage />} />
        <Route path="accounting/payouts/new" element={<ComputePayoutPage />} />
        <Route path="accounting/payouts/:id/adjust" element={<PayoutAdjustmentPage />} />
        <Route path="accounts/authority" element={<FinancialAuthorityPage />} />
        <Route path="accounts/authority/changes/new" element={<AuthorityChangePage />} />
        <Route path="accounts/doctor-share" element={<DoctorSharePage />} />
        <Route path="accounts/doctor-share/changes/new" element={<ShareChangePage />} />
        <Route path="account/security" element={<SecurityPage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="people/new" element={<PersonPage />} />
        <Route path="people/roles/new" element={<RolePage />} />
        <Route path="people/roles/:roleId/edit" element={<RolePage />} />
        <Route path="people/leave/new" element={<RequestLeavePage />} />
        <Route path="people/attendance/new" element={<MarkAttendancePage />} />
        <Route path="people/payroll/new" element={<RunPayrollPage />} />
        <Route path="people/rosters/new" element={<NewRosterPage />} />
        <Route path="people/rosters/:rosterId/shifts/new" element={<AssignShiftPage />} />
        <Route path="people/:personKey/edit" element={<PersonPage />} />
        <Route path="hr" element={<Navigate to="/people?tab=roster" replace />} />
        <Route path="mrd" element={<MrdPage />} />
        <Route path="mrd/requests/new" element={<LogRequestPage />} />

        <Route path="reports" element={<ReportsPage />} />

        <Route path="admin/users" element={<Navigate to="/people" replace />} />
        <Route path="admin/branches" element={<BranchesPage />} />
        <Route path="admin/branches/new" element={<BranchFormPage />} />
        <Route path="admin/branches/:branchId/edit" element={<BranchFormPage />} />
        <Route path="admin/departments/new" element={<DepartmentPage />} />
        <Route path="admin/departments/:departmentId/edit" element={<DepartmentPage />} />
        <Route path="admin/specialties/new" element={<SpecialtyPage />} />
        <Route path="admin/specialties/:specialtyId/edit" element={<SpecialtyPage />} />
        <Route path="admin/stores/new" element={<StorePage />} />
        <Route path="admin/stores/:storeId/edit" element={<StorePage />} />
        <Route path="admin/wards/new" element={<WardPage />} />
        <Route path="admin/wards/:wardId/edit" element={<WardPage />} />
        <Route path="admin/beds/new" element={<BedPage />} />
        <Route path="admin/beds/:bedId/edit" element={<BedPage />} />
        <Route path="admin/settings" element={<SettingsPage />} />
        <Route path="admin/emr-phrases/:departmentId" element={<DepartmentPhrasesPage />} />
        <Route path="admin/listing" element={<PublicListingPage />} />
        <Route path="admin/listing/edit" element={<ListingPage />} />
        <Route path="admin/audit" element={<AuditPage />} />

        {/* Client portal — a contact person's whole surface. */}
        <Route path="client" element={<ClientHomePage />} />
        <Route path="client/tickets" element={<ClientTicketsPage />} />
        <Route path="client/tickets/new" element={<ClientNewTicketPage />} />
        <Route path="client/renewals" element={<ClientRenewalsPage />} />
        <Route path="client/renewals/new" element={<RenewalPage />} />

        {/* Support, from inside a hospital. */}
        <Route path="support" element={<TenantSupportPage />} />
        <Route path="support/tickets/new" element={<TenantNewTicketPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
